import { extractStyledItems } from './extract.js';
import { computePageFontStats } from './font-analysis.js';
import { detectColumns } from './transformers/columns.js';
import { groupIntoLines } from './transformers/lines.js';
import { groupIntoParagraphs } from './transformers/paragraphs.js';
import { classifyHeading } from './transformers/headings.js';
import { classifyListLine } from './transformers/lists.js';
import { itemsToRuns, mergeLineRuns } from './transformers/emphasis.js';
import { detectTable } from './transformers/tables.js';
import { renderBlocks } from './render.js';
/**
 * Tier 2 pipeline orchestration.
 *
 * Stage order matters:
 *   1. extract    pdf.js → StyledTextItem[]    (with resolved fonts + style flags)
 *   2. fontStats  per-page median heights      (basis for heading thresholds)
 *   3. columns    multi-column detection       (page sliced into independent flows)
 *   4. lines      group items by y per column  (reading order)
 *   5. paragraphs group lines by gap + height  (paragraph boundaries)
 *   6. classify   each paragraph → heading? list? table? plain?
 *   7. emit       Block[] in reading order
 *   8. render     Block[] → markdown
 */
export async function parsePdfTier2(buffer) {
    const start = Date.now();
    const warnings = [];
    const { items, pageCount } = await extractStyledItems(buffer);
    if (items.length === 0) {
        warnings.push('no extractable text — PDF may be a scanned image or empty');
        return {
            markdown: '',
            meta: {
                sourceFormat: 'pdf',
                tier: 'tier2',
                pageCount,
                durationMs: Date.now() - start,
                warnings,
            },
        };
    }
    const fontStats = computePageFontStats(items);
    const columns = detectColumns(items);
    const lines = groupIntoLines(columns);
    const paragraphs = groupIntoParagraphs(lines);
    const blocks = [];
    let lastPage = 0;
    for (const group of paragraphs) {
        if (group.page !== lastPage) {
            // Mark page transitions so downstream consumers can preserve them if useful.
            const pb = { kind: 'pageBreak', page: group.page };
            blocks.push(pb);
            lastPage = group.page;
        }
        const block = classifyParagraph(group, fontStats.get(group.page));
        if (block)
            blocks.push(block);
    }
    // Per-page low-density warnings.
    emitLowDensityWarnings(items, pageCount, warnings);
    const markdown = renderBlocks(blocks);
    return {
        markdown,
        meta: {
            sourceFormat: 'pdf',
            tier: 'tier2',
            pageCount,
            durationMs: Date.now() - start,
            warnings,
        },
    };
}
/** Decide what kind of block a ParagraphGroup represents and build it. */
function classifyParagraph(group, pageStats) {
    if (group.lines.length === 0)
        return null;
    // 1) Heading test (small, top-priority).
    const headingLevel = classifyHeading(group, pageStats);
    if (headingLevel === 1 || headingLevel === 2 || headingLevel === 3 || headingLevel === 4) {
        const headingBlock = {
            kind: 'heading',
            level: headingLevel,
            runs: mergeLineRuns(group.lines.map((l) => itemsToRuns(l.items))),
        };
        return headingBlock;
    }
    // 2) Table test (multiple lines + grid alignment).
    if (group.lines.length >= 2) {
        const table = detectTable(group.lines);
        if (table)
            return table;
    }
    // 3) List test (any line in the group is a list line → treat the whole group as a list).
    const baselineX = Math.min(...group.lines.map((l) => l.items[0]?.x ?? Infinity));
    const columnXMin = baselineX; // column origin inherited from first item
    const listInfos = group.lines.map((l) => classifyListLine(l, columnXMin, baselineX));
    const listLineCount = listInfos.filter((info) => info.isList).length;
    if (listLineCount > 0 && listLineCount / group.lines.length >= 0.5) {
        const ordered = listInfos.find((info) => info.isList && info.ordered) !== undefined;
        const listItems = [];
        for (let i = 0; i < group.lines.length; i++) {
            const info = listInfos[i];
            if (info.isList) {
                const li = { depth: info.depth, runs: info.runs };
                if (info.marker)
                    li.marker = info.marker;
                listItems.push(li);
            }
            else {
                // Continuation line — append its runs to the previous list item if any.
                if (listItems.length > 0) {
                    const prev = listItems[listItems.length - 1];
                    const newRuns = itemsToRuns(group.lines[i].items);
                    for (const r of newRuns)
                        prev.runs.push(r);
                }
            }
        }
        if (listItems.length > 0) {
            const list = { kind: 'list', ordered, items: listItems };
            return list;
        }
    }
    // 4) Default: paragraph.
    const runs = mergeLineRuns(group.lines.map((l) => itemsToRuns(l.items)));
    if (runs.length === 0)
        return null;
    const para = { kind: 'paragraph', runs };
    return para;
}
/** Emit "low text density on page N" warnings — likely scanned pages. */
function emitLowDensityWarnings(items, pageCount, warnings) {
    const charCountByPage = new Map();
    for (const it of items) {
        charCountByPage.set(it.page, (charCountByPage.get(it.page) ?? 0) + it.str.length);
    }
    for (let p = 1; p <= pageCount; p++) {
        const chars = charCountByPage.get(p) ?? 0;
        if (chars < 50) {
            warnings.push(`page ${p}: low text density (${chars} chars) — may be a scanned image`);
        }
    }
}
//# sourceMappingURL=pipeline.js.map